// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Calculator enum definitions for edge types, adhesives types etc..
 * @author James Reid
 */

// @ts-check

// @@no-imports

// @@body
/**
 * Product display category string (loosely related to product types) which is
 * used for rendering bom tables.
 * @typedef {"Membranes"|"PVC Trims"|"Metal Trims"|"Tapes"|"Deck Adhesives"|"Primers"|"Sealants"|"Drains"|"Installation Kits"|"Extras"|"Other"} ProductDisplayCategory // @no-wrap
 */

/**
 * Membrane types available in calculator.
 * @typedef {"onePiece"|"fleeceBacked"} MembraneType
 */

/**
 * Possible seam directions when tiling deck with multiple membranes.
 * @typedef {"any"|"horizontal"|"vertical"} SeamDirection
 */

/**
 * Roof deck substrate types for with the calculator can specify materials.
 * @typedef {"osb"|"grp"|"metal"|"felt"} SubstrateType
 */

/**
 * Roof edge string types available in calculator.
 * @typedef {"houseWall"|"parapetWall"|"gutterDrip"|"raisedKerb"|"pitchRoofOn"|"pitchRoofOff"|"feltJoin"|"seamJoin"|"buttJoin"|"none"} EdgeType // @no-wrap
 */

/**
 * Roof deck adhesive string types available in calculator.
 * @typedef {"waterBased"|"contactBonding"|"pu"} DeckAdhesiveType
 */

/**
 * Roof primer adhesive string types available in calculator.
 * @typedef {"primer"} PrimerAdhesiveType
 */

/**
 * Roof sealant adhesive string types available in calculator.
 * @typedef {"lapEdge"|"waterBlock"|"msPolymer"|"blackSilicone"} SealantAdhesiveType // @no-wrap
 */

/**
 * All roof adhesive string types available in calculator.
 * @typedef {DeckAdhesiveType|PrimerAdhesiveType|SealantAdhesiveType} AdhesiveType // @no-wrap
 */

/**
 * Adhesive categories referring to where/how the adhesive is applied.
 * @typedef {"deck"|"primer"|"sealants"} AdhesiveCategory
 */

/**
 * Application methods for sealant products.
 * @typedef {"roller"|"aerosol"|"spray"|"sealantGun"} AdhesiveApplications
 */

/**
 * Tape types available in the calculator.
 * @typedef {"seam"|"cured"|"uncured"|"mechanical"} TapeType
 */

/**
 * Patch sized available in the calculator which will refer to different amounts
 * of overhang being specified on a given feature.
 * @typedef {"extraSmall"|"small"|"medium"|"large"|"extraLarge"} PatchSize
 */

/**
 * Patch types for corners and terminations available in the calculator.
 * @typedef {"internal"|"external"} PatchType
 */

/**
 * Rubber join string types available in calculator.
 * @typedef {"seamJoin"|"buttJoin"} JoinType
 */

/**
 * Trim type string types available in calculator.
 * @typedef {"cellular"|"solid"} TrimType
 */

/**
 * Trim materials available in the calculator.
 * @typedef {"pvc"|"metal"} TrimMaterial
 */

/**
 * Trim color string types available in calculator.
 * @typedef {"black"|"grey"} TrimColor
 */

/**
 * Trim depths available in the calculator referring to the depth a trim will
 * penetrate into a house or parapet wall.
 * @typedef {"standard"|"deep"} TrimDepth
 */

/**
 * Drain types which the calculator can handle.
 * @typedef {"balcony"|"horizontal"|"horizontalLarge"|"horizontalSmall"|"horizontalExtender"|"pipeSeal"|"pipeBox"|"internal"|"top"|"breather"} DrainType // @no-wrap
 */

/**
 * Tool types available in the calculator.
 * @typedef {"adhesiveRollers"|"rubberRollers"|"brushes"|"guns"|"scissors"|"scrubbers"|"other"|"kits"} ToolExtraType // @no-wrap
 */

/**
 * Cleaners available in the calculator.
 * @typedef {"cleaners"} CleanerExtraType
 */

/**
 * Joiners available in the calculator.
 * @typedef {"joiners"} JoinerExtraType
 */

/**
 * Fixings available in the calculator.
 * @typedef {"fixings"} FixingExtraType
 */

/**
 * Installation kits available in the calculator.
 * @typedef {"rooflight"|"internalDrain"|"horizontalDrain"|"pipeSeal"} KitExtraType // @no-wrap
 */

/**
 * All extra products available in the calculator.
 * @typedef {ToolExtraType|CleanerExtraType|JoinerExtraType|FixingExtraType|KitExtraType} ExtraType // @no-wrap
 */

// @@exports
/**
 * @type {ProductDisplayCategory}
 * @ignore
 */
export let ProductDisplayCategory

/**
 * @type {MembraneType}
 * @ignore
 */
export let MembraneType

/**
 * @type {SeamDirection}
 * @ignore
 */
export let SeamDirection

/**
 * @type {SubstrateType}
 * @ignore
 */
export let SubstrateType

/**
 * @type {EdgeType}
 * @ignore
 */
export let EdgeType

/**
 * @type {DeckAdhesiveType}
 * @ignore
 */
export let DeckAdhesiveType

/**
 * @type {PrimerAdhesiveType}
 * @ignore
 */
export let PrimerAdhesiveType

/**
 * @type {SealantAdhesiveType}
 * @ignore
 */
export let SealantAdhesiveType

/**
 * @type {AdhesiveType}
 * @ignore
 */
export let AdhesiveType

/**
 * @type {AdhesiveCategory}
 * @ignore
 */
export let AdhesiveCategory

/**
 * @type {AdhesiveApplications}
 * @ignore
 */
export let AdhesiveApplications

/**
 * @type {TapeType}
 * @ignore
 */
export let TapeType

/**
 * @type {PatchSize}
 * @ignore
 */
export let PatchSize

/**
 * @type {PatchType}
 * @ignore
 */
export let PatchType

/**
 * @type {JoinType}
 * @ignore
 */
export let JoinType

/**
 * @type {TrimType}
 * @ignore
 */
export let TrimType

/**
 * @type {TrimMaterial}
 * @ignore
 */
export let TrimMaterial

/**
 * @type {TrimColor}
 * @ignore
 */
export let TrimColor

/**
 * @type {TrimDepth}
 * @ignore
 */
export let TrimDepth

/**
 * @type {DrainType}
 * @ignore
 */
export let DrainType

/**
 * @type {ToolExtraType}
 * @ignore
 */
export let ToolExtraType

/**
 * @type {CleanerExtraType}
 * @ignore
 */
export let CleanerExtraType

/**
 * @type {JoinerExtraType}
 * @ignore
 */
export let JoinerExtraType

/**
 * @type {FixingExtraType}
 * @ignore
 */
export let FixingExtraType

/**
 * @type {KitExtraType}
 * @ignore
 */
export let KitExtraType

/**
 * @type {ExtraType}
 * @ignore
 */
export let ExtraType

/** @enum {ProductDisplayCategory} */
export const PRODUCT_DISPLAY_CATEGORIES = {
    membrane: /** @type {ProductDisplayCategory} */ ("Membranes"),
    pvcTrim: /** @type {ProductDisplayCategory} */ ("PVC Trims"),
    metalTrim: /** @type {ProductDisplayCategory} */ ("Metal Trims"),
    tape: /** @type {ProductDisplayCategory} */ ("Tapes"),
    deckAdhesive: /** @type {ProductDisplayCategory} */ ("Deck Adhesives"),
    primer: /** @type {ProductDisplayCategory} */ ("Primers"),
    sealant: /** @type {ProductDisplayCategory} */ ("Sealants"),
    drain: /** @type {ProductDisplayCategory} */ ("Drains"),
    kit: /** @type {ProductDisplayCategory} */ ("Installation Kits"),
    extra: /** @type {ProductDisplayCategory} */ ("Extras"),
    other: /** @type {ProductDisplayCategory} */ ("Other")
}

/** @enum {MembraneType} */
export const MEMBRANE_TYPES = {
    onePiece: /** @type {MembraneType} */ ("onePiece"),
    fleeceBacked: /** @type {MembraneType} */ ("fleeceBacked")
}

/** @enum {SeamDirection} */
export const SEAM_DIRECTIONS = {
    any: /** @type {SeamDirection} */ ("any"),
    horizontal: /** @type {SeamDirection} */ ("horizontal"),
    vertical: /** @type {SeamDirection} */ ("vertical")
}

/** @enum {SubstrateType} */
export const SUBSTRATE_TYPES = {
    osb: /** @type {SubstrateType} */ ("osb"),
    grp: /** @type {SubstrateType} */ ("grp"),
    metal: /** @type {SubstrateType} */ ("metal"),
    felt: /** @type {SubstrateType} */ ("felt")
}

/**
 * Object of membrane configurations available in the calculator.
 */
export const MEMBRANES = {
    onePiece_1_14: {
        type: /** @type {MembraneType} */ ("onePiece"),
        thickness: 0.00114,
        rollLength: 30.5,
        title: "1.14mm Standard FlexiProof One Piece EPDM Membrane",
        id: "1-14-fp",
        priority: 2
    },
    onePiece_1_20: {
        type: /** @type {MembraneType} */ ("onePiece"),
        thickness: 0.00120,
        rollLength: 30.5,
        title: "1.20mm Premium FlexiProof One Piece EPDM Membrane",
        id: "1-20-fp",
        priority: 1
    },
    onePiece_1_52: {
        type: /** @type {MembraneType} */ ("onePiece"),
        thickness: 0.00152,
        rollLength: 30.5,
        title: "1.52mm Heavy Duty FlexiProof One Piece EPDM Membrane",
        id: "1-52-fp",
        priority: 3
    },
    fleeceBacked_1_20: {
        type: /** @type {MembraneType} */ ("fleeceBacked"),
        thickness: 0.00120,
        rollLength: 61,
        title: "2.60mm Heavy Duty FlexiProof Fleece Backed EPDM Membrane",
        id: "1-20-fb",
        priority: 4
    }
}

/**
 * Object of PVC trim configurations available in the calculator.
 */
export const TRIMS = {
    cellularBlackShort: {
        type: /** @type {TrimType} */ ("cellular"),
        color: /** @type {TrimColor} */ ("black"),
        maxLength: 2,
        priority: 1
    },
    cellularBlack: {
        type: /** @type {TrimType} */ ("cellular"),
        color: /** @type {TrimColor} */ ("black"),
        minLength: 2.5,
        priority: 2
    },
    solidBlack: {
        type: /** @type {TrimType} */ ("solid"),
        color: /** @type {TrimColor} */ ("black"),
        minLength: 2.5,
        priority: 3
    },
    solidGrey: {
        type: /** @type {TrimType} */ ("solid"),
        color: /** @type {TrimColor} */ ("grey"),
        minLength: 2.5,
        priority: 4
    }
}

/** @enum {EdgeType} */
export const EDGE_TYPES = {
    houseWall: /** @type {EdgeType} */ ("houseWall"),
    parapetWall: /** @type {EdgeType} */ ("parapetWall"),
    gutterDrip: /** @type {EdgeType} */ ("gutterDrip"),
    raisedKerb: /** @type {EdgeType} */ ("raisedKerb"),
    pitchRoofOn: /** @type {EdgeType} */ ("pitchRoofOn"),
    pitchRoofOff: /** @type {EdgeType} */ ("pitchRoofOff"),
    feltJoin: /** @type {EdgeType} */ ("feltJoin"),
    seamJoin: /** @type {EdgeType} */ ("seamJoin"),
    buttJoin: /** @type {EdgeType} */ ("buttJoin"),
    none: /** @type {EdgeType} */ ("none")
}

/**
 * Nested adhesives enum containing all available adhesive products used by the
 * calculator.
 */
export const ADHESIVE_TYPES = {
    deck: /** @enum {DeckAdhesiveType} */ ({
        waterBased: /** @type {DeckAdhesiveType} */ ("waterBased"),
        contactBonding: /** @type {DeckAdhesiveType} */ ("contactBonding"),
        pu: /** @type {DeckAdhesiveType} */ ("pu")
    }),
    primer: /** @type {PrimerAdhesiveType} */ ("primer"),
    sealants: /** @enum {SealantAdhesiveType} */ ({
        lapEdge: /** @type {SealantAdhesiveType} */ ("lapEdge"),
        waterBlock: /** @type {SealantAdhesiveType} */ ("waterBlock"),
        msPolymer: /** @type {SealantAdhesiveType} */ ("msPolymer"),
        blackSilicone: /** @type {SealantAdhesiveType} */ ("blackSilicone")
    })
}

/** @enum {AdhesiveCategory} */
export const ADHESIVE_CATEGORIES = {
    deck: /** @type {AdhesiveCategory} */ ("deck"),
    primer: /** @type {AdhesiveCategory} */ ("primer"),
    sealants: /** @type {AdhesiveCategory} */ ("sealants")
}

/** @enum {AdhesiveApplications} */
export const ADHESIVE_APPLICATIONS = {
    roller: /** @type {AdhesiveApplications} */ ("roller"),
    aerosol: /** @type {AdhesiveApplications} */ ("aerosol"),
    spray: /** @type {AdhesiveApplications} */ ("spray"),
    sealantGun: /** @type {AdhesiveApplications} */ ("sealantGun")
}

/** @enum {TapeType} */
export const TAPE_TYPES = {
    seam: /** @type {TapeType} */ ("seam"),
    cured: /** @type {TapeType} */ ("cured"),
    uncured: /** @type {TapeType} */ ("uncured"),
    mechanical: /** @type {TapeType} */ ("mechanical")
}

/** @enum {PatchSize} */
export const PATCH_SIZES = {
    extraSmall: /** @type {PatchSize} */ ("extraSmall"),
    small: /** @type {PatchSize} */ ("small"),
    medium: /** @type {PatchSize} */ ("medium"),
    large: /** @type {PatchSize} */ ("large"),
    extraLarge: /** @type {PatchSize} */ ("extraLarge")
}

/** @enum {PatchType} */
export const PATCH_TYPES = {
    internal: /** @type {PatchType} */ ("internal"),
    external: /** @type {PatchType} */ ("external")
}

/** @enum {JoinType} */
export const JOIN_TYPES = {
    seam: /** @type {JoinType} */ ("seamJoin"),
    butt: /** @type {JoinType} */ ("buttJoin")
}

/** @enum {TrimType} */
export const TRIM_TYPES = {
    cellular: /** @type {TrimType} */ ("cellular"),
    solid: /** @type {TrimType} */ ("solid")
}

/** @enum {TrimMaterial} */
export const TRIM_MATERIALS = {
    pvc: /** @type {TrimMaterial} */ ("pvc"),
    metal: /** @type {TrimMaterial} */ ("metal")
}

/** @enum {TrimColor} */
export const TRIM_COLORS = {
    black: /** @type {TrimColor} */ ("black"),
    grey: /** @type {TrimColor} */ ("grey")
}

/** @enum {TrimDepth} */
export const TRIM_DEPTHS = {
    standard: /** @type {TrimDepth} */ ("standard"),
    deep: /** @type {TrimDepth} */ ("deep")
}

/** @enum {DrainType} */
export const DRAIN_TYPES = {
    balcony: /** @type {DrainType} */ ("balcony"),
    horizontal: /** @type {DrainType} */ ("horizontal"),
    horizontalLarge: /** @type {DrainType} */ ("horizontalLarge"),
    horizontalSmall: /** @type {DrainType} */ ("horizontalSmall"),
    horizontalExtender: /** @type {DrainType} */ ("horizontalExtender"),
    pipeSeal: /** @type {DrainType} */ ("pipeSeal"),
    pipeBox: /** @type {DrainType} */ ("pipeBox"),
    internal: /** @type {DrainType} */ ("internal"),
    top: /** @type {DrainType} */ ("top"),
    breather: /** @type {DrainType} */ ("breather"),
}

/**
 * Nested enum of all extra product types available.
 */
export const EXTRA_TYPES = {
    tools: /** @enum {ToolExtraType} */ ({
        adhesiveRollers: /** @type {ToolExtraType} */ ("adhesiveRollers"),
        rubberRollers: /** @type {ToolExtraType} */ ("rubberRollers"),
        brushes: /** @type {ToolExtraType} */ ("brushes"),
        guns: /** @type {ToolExtraType} */ ("guns"),
        scissors: /** @type {ToolExtraType} */ ("scissors"),
        scrubbers: /** @type {ToolExtraType} */ ("scrubbers"),
        other: /** @type {ToolExtraType} */ ("other"),
        kits: /** @type {ToolExtraType} */ ("kits"),
    }),
    cleaners: /** @type {CleanerExtraType} */ ("cleaners"),
    joiners: /** @type {JoinerExtraType} */ ("joiners"),
    fixings: /** @type {FixingExtraType} */ ("fixings"),
    kits: {
        rooflight: /** @type {KitExtraType} */ ("rooflight"),
        internalDrain: /** @type {KitExtraType} */ ("internalDrain"),
        horizontalDrain: /** @type {KitExtraType} */ ("horizontalDrain"),
        pipeSeal: /** @type {KitExtraType} */ ("pipeSeal")
    }
}
